import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SendIcon from '@mui/icons-material/Send';
import { Box, Button, Stack } from '@mui/material';
import { get } from 'lodash';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useApplication } from '../../../../context/ApplicationContext';
import { APIS } from '../../../../lib/constants/apis';
import { ANTRAG_TYP, ANTRAGS_STATUS } from '../../../../lib/constants/constant';
import { GetFieldErrors } from '../../../../lib/validation/ApplicationValidator';
import MuiDialog from '../../mui-dialog/MuiDialog';
import { constructHierarchyArray, extractIds } from './TreeNavigatrionHelper';

// import Schemas
import AntragSchema from '../../../../lib/paths/antrag_FULL_Paths.json';
import VerwendungsNachweisSchema from '../../../../lib/paths/verwendungsnachweis_FULL_Paths.json';
const P = AntragSchema.attributes;
const VN_P = VerwendungsNachweisSchema.attributes;

type TreeViewNavigationProps = {
	treeStructure: any;
	currentNode: string[];
	handleNavigation: (nodeIds: string[], buttonClick?: string) => void;
	hasCurrentContentError: () => boolean;
	validateCompleteForm: () => void;
};
const TreeNavigationButtons: FC<TreeViewNavigationProps> = ({
	treeStructure,
	currentNode,
	handleNavigation,
	hasCurrentContentError,
	validateCompleteForm,
}) => {
	const location = useLocation();
	const { application, saveApplication, readOnly } = useApplication();
	const [previousNodes, setPreviousNodes] = useState<string[]>(['1']);
	const [nextNodes, setNextNodes] = useState<string[]>(['1']);
	const [hasError, setHasError] = useState(false);

	const [submitDialogOpen, setSubmitDialogOpen] = useState<boolean>(false);

	const [canSubmitZuwendungsvertrag, setCanSubmitZuwendungsvertrag] = useState<boolean>(false);
	const [skipValidation, setSkipValidation] = useState<boolean>(false);

	const handleSubmitApp = async () => {
		setSubmitDialogOpen(false);
		saveApplication(true);
	};

	const DialogTitle = useMemo(() => {
		switch (get(location, 'state.api')) {
			case APIS.Antrag:
				if (get(application, P.Status_Status.path) === ANTRAGS_STATUS.vertragUebermittel) return 'Vertrag übermitteln';
				return 'Antrag einreichen';
			case APIS.Verwendungsnachweis:
				if (get(application, VN_P.Allgemein_Nachweistyp.path) === ANTRAG_TYP.Zwischenmeldung) return 'Zwischenmeldung einreichen';
				return 'Verwendungsnachweis einreichen';
			default:
				return 'Antrag einreichen';
		}
	}, [application, location]);
	const DialogContent = useMemo(() => {
		switch (get(location, 'state.api')) {
			case APIS.Antrag:
				if (get(application, P.Status_Status.path) === ANTRAGS_STATUS.vertragUebermittel)
					return 'Stellen Sie bitte sicher, dass Sie den Vertrag unterschrieben und korrekt hochgeladen haben. \n\n Möchten Sie fortfahren?';
				return 'Sind Sie sicher, dass Sie den Antrag einreichen möchten?';
			case APIS.Verwendungsnachweis:
				if (get(application, VN_P.Allgemein_Nachweistyp.path) === ANTRAG_TYP.Zwischenmeldung)
					return 'Sind Sie sicher, dass Sie die Zwischenmeldung einreichen möchten?';
				return 'Sind Sie sicher, dass Sie den Verwendungsnachweis einreichen möchten?';
			default:
				return 'Sind Sie sicher, dass Sie den Antrag einreichen möchten?';
		}
	}, [application, location]);

	useEffect(() => {
		if (!currentNode || currentNode.length === 0) return;

		const allNodesIdsArr = extractIds(treeStructure);
		const currentNodeStr = currentNode[currentNode.length - 1];
		const currentNodeIndex = allNodesIdsArr.findIndex((ele: any) => ele === currentNodeStr);

		if (currentNodeIndex === -1) return;
		let prevNodeStr = allNodesIdsArr[currentNodeIndex - 1];
		let nextNodeStr = allNodesIdsArr[currentNodeIndex + 1];
		// Update state
		setPreviousNodes(constructHierarchyArray(prevNodeStr));
		setNextNodes(constructHierarchyArray(nextNodeStr));
		if (get(application, P.Status_Status.path) === ANTRAGS_STATUS.vertragUebermittel) {
			setCanSubmitZuwendungsvertrag(true);
			setSkipValidation(!canSubmitZuwendungsvertrag);
		}
	}, [treeStructure, currentNode]);

	const hasAnyErrorInForm = (): boolean => {
		const errorList = GetFieldErrors(treeStructure, application, skipValidation);
		return errorList.length > 0;
	};

	const einreichen = () => {
		setSubmitDialogOpen(true);
		hasCurrentContentError();
		validateCompleteForm();
		setHasError(hasAnyErrorInForm());
	};

	const showSubmitButton: Boolean = useMemo(() => {
		if (
			get(application, P.Status_Status.path) === ANTRAGS_STATUS.vertragUebermittel &&
			!get(application, P.Zuwendungsvertarg_Unterschrieben_Am.path, '')
		) {
			return true;
		} else {
			return !readOnly;
		}
	}, [application, readOnly]);

	return (
		<>
			<MuiDialog
				open={submitDialogOpen}
				onClose={() => setSubmitDialogOpen(false)}
				onConfirm={() => handleSubmitApp()}
				title={hasError ? 'Fehler im Formular' : DialogTitle}
				content={hasError ?
					canSubmitZuwendungsvertrag ?
						'Der Antrag kann nicht ohne den Zuwendungsvertrag eingereicht werden. Bitte laden Sie den unterschriebenen Zuwendungsvertrag hoch.'
						:
						'Es befinden sich noch einige Fehler im Formular' : DialogContent}
				hasError={hasError}
			/>
			<Box className='detailNavigationButtons'>
				<Stack sx={{ mt: '2rem' }} direction={'row'} justifyContent={'space-between'}>
					<Button
						variant='secondaryButton'
						disabled={previousNodes[0] === undefined}
						onClick={() => {
							handleNavigation([], 'previous');
						}}
						startIcon={<ArrowBackIcon />}
						sx={{ pointerEvents: 'initial' }}
					>
						Zurück
					</Button>

					{nextNodes[0] !== undefined ?
						<Button
							variant='contained'
							onClick={() => handleNavigation([], 'next')}
							endIcon={<ArrowForwardIcon />}
							sx={{ pointerEvents: 'initial' }}
						>
							Weiter
						</Button>
						: <>
							{showSubmitButton && (
								<Button endIcon={<SendIcon />} onClick={() => einreichen()} sx={{ pointerEvents: 'initial' }}>
									Einreichen
								</Button>
							)}
						</>
					}
				</Stack>
			</Box>
		</>
	);
};
export default TreeNavigationButtons;
